import { HttpErrorResponse } from '@angular/common/http';
import { Component, Inject, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
} from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { AdminBackend } from '@libs/src/admin/admin.backend';

import { Account } from '@libs/src/models/account.model';
import { sanitizeUrlWithProtocol } from '@libs/src/utilities/url.utilities';
import { AccountEditDialogData } from '@main-client/src/app/account-edit-dialog/account-edit-dialog-data';

import get from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import isEqual from 'lodash-es/isEqual';

@Component({
  selector: 'app-account-edit-dialog',
  templateUrl: './account-edit-dialog.component.html',
})
export class AccountEditDialogComponent implements OnInit {
  accountForm: UntypedFormGroup;
  fieldsSavingTracker: { [fieldName: string]: boolean } = {};

  constructor(
    private adminBackend: AdminBackend,
    private fb: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: AccountEditDialogData,
  ) {}

  ngOnInit() {
    this.buildForm();
  }

  private buildForm() {
    const account = this.data.account;
    this.accountForm = this.fb.group({
      first_name: [account.first_name],
      last_name: [account.last_name],
      email: [account.email],
      about: [account.about],
      experience: this.fb.array([
        this.fb.group({
          _id: [get(account, ['experience', '0', '_id'])],
          job_title: [get(account, ['experience', '0', 'job_title'])],
          organization_name: [
            get(account, ['experience', '0', 'organization_name']),
          ],
        }),
      ]),
      education: this.fb.array([
        this.fb.group({
          _id: [get(account, ['education', '0', '_id'])],
          program: [get(account, ['education', '0', 'program'])],
          school: [get(account, ['education', '0', 'school'])],
        }),
      ]),
      website: [account.website],
    });
  }

  updateAccount(fieldName: string, control: AbstractControl | any) {
    const updateValue = this.sanitizeValue(fieldName, control.value);
    if (
      !control.valid ||
      isEqual(updateValue, (this.data.account as any)[fieldName]) ||
      this.fieldsSavingTracker[fieldName]
    ) {
      return;
    }

    const account = this.data.account;
    const update = {
      [fieldName]: updateValue,
    };
    this.fieldsSavingTracker[fieldName] = true;
    this.adminBackend
      .patchAccount(account, update)
      .pipe(
        tap((response: Account) => {
          this.fieldsSavingTracker[fieldName] = false;
          this.data.account = {
            ...account,
            ...response,
          };
          this.buildForm();
          if (this.data.onUpdate) {
            this.data.onUpdate(this.data.account);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.fieldsSavingTracker[fieldName] = false;
          control.setErrors({ server: error.error.message });
          return throwError(error);
        }),
      )
      .subscribe();
  }

  updateAccountSubDocument(
    fieldName: string,
    rootField: string,
    subDocumentId: string,
    control: AbstractControl | any,
  ) {
    const account = this.data.account;
    const updateValue = control.value;

    if (!control.valid || this.fieldsSavingTracker[fieldName]) {
      return;
    }
    const update = {
      [fieldName]: updateValue,
    };
    const operation = subDocumentId
      ? this.adminBackend.putAccountSubDocument(
          account,
          rootField,
          subDocumentId,
          update,
        )
      : this.adminBackend.postAccountSubDocument(account, rootField, update);
    operation
      .pipe(
        tap((response: any) => {
          this.fieldsSavingTracker[fieldName] = false;
          this.data.account = {
            ...account,
            ...{
              [rootField]: {
                ...(account as any)[rootField],
                ...response,
              },
            },
          };
          this.buildForm();
          if (this.data.onUpdate) {
            this.data.onUpdate(this.data.account);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          this.fieldsSavingTracker[fieldName] = false;
          control.setErrors({ server: error.error.message });
          return throwError(error);
        }),
      )
      .subscribe();
  }

  shouldShowSave(control: AbstractControl | any, currentValue: string) {
    const controlValue = control && control.value;
    return (
      (!isEmpty(controlValue) || !isEmpty(currentValue)) &&
      !isEqual(controlValue, currentValue)
    );
  }

  sanitizeValue(fieldName: string, value: string) {
    return isEqual('website', fieldName)
      ? sanitizeUrlWithProtocol(value)
      : value;
  }
}
